// Shapes shared by the server and the web app. Kept dependency-free so both can import them.

/** One vocabulary item, as emitted by scripts/build-vocab.mjs (keys are short: this ships to the browser). */
export interface Word {
  /** Simplified form — the card's front. */
  s: string;
  /** Traditional form, present only when it differs from the simplified one. */
  t?: string;
  /** Tone-marked pinyin, syllables space-separated: "wǒ men". */
  p: string;
  /** Tone per syllable, 1-4 with 5 for the neutral tone. Always the same length as `p`'s syllables. */
  tones: number[];
  /** Up to three English glosses, most central first. */
  e: string[];
  /** Coarse part of speech: "verb", "noun", "measure word", … */
  pos: string;
  /**
   * How the word conjugates, in JMdict's own terms: v1, v5r, v5k-s, vk, vs-i, adj-i, adj-na.
   *
   * WHY IT IS DATA AND NOT A RULE. A card cannot derive this. 見る is v1 and 走る is v5r, both end
   * in る, and their て forms are 見て and 走って — the difference is a fact about each word that
   * only a dictionary holds. Absent where JMdict does not say, and a card with no class shows no
   * conjugation table rather than a guessed one (MYN-180).
   *
   * Japanese only. Chinese does not inflect, and its corpus carries none of these.
   */
  c?: string;
  /** Corpus frequency rank; 1 is 的. This is what orders every deck easy -> hard. */
  r: number;
  /** HSK band 1-7 (7 covers the 7-9 range), or 0 when the word sits outside the syllabus. */
  hsk: number;
}

export interface Deck {
  id: string;
  name: string;
  blurb: string;
  /** The part of speech this deck filters on, or null for the frequency decks. */
  pos: string | null;
  /** Indices into `Vocab.words`, already in easy -> hard order. */
  words: number[];
}

export interface Vocab {
  version: number;
  generatedAt: string;
  source: { name: string; url: string; license: string };
  decks: Deck[];
  words: Word[];
}

/** One word inside a sentence, as produced by the segmenter. */
export interface SentenceWord {
  /** The word's characters. */
  t: string;
  /**
   * The dictionary form, when the word in the sentence is not it.
   *
   * Absent for Chinese, which does not inflect: 走 is 走 wherever it stands. English is the reason
   * this exists — a sentence says "going" and the vocabulary teaches "go", so without somewhere to
   * put the base form a tapped word finds nothing and the gap drill skips the sentence. Set by the
   * generator, which is the only place that can know it.
   */
  b?: string;
  /**
   * The word AS WRITTEN, when that differs from the key it is filed under.
   *
   * `t` is lower case because the vocabulary is, and everything that matches a word — knownWords,
   * lookupWord, the audio filenames — matches on it. Rendering from `t` alone made every English
   * sentence and story read "i have a kitten". Absent for Chinese, which has no case, and absent
   * wherever the written form and the key are the same string.
   *
   * Display only: never look a word up by it.
   */
  d?: string;
  /** Tone-marked pinyin, syllables space-separated. */
  p: string;
  /** Tone per syllable, 1-4 with 5 for neutral. */
  tones: number[];
}

/** A statement sentence with its English translation. */
export interface Sentence {
  /** The Chinese, punctuation intact. */
  s: string;
  /** The English translation. */
  en: string;
  /** The sentence split into words — what makes "a 3-word sentence" a meaningful category. */
  w: SentenceWord[];
  /** Word count, i.e. `w.length`. Stored because the decks are built on it. */
  n: number;
  /** Difficulty: the frequency rank of the hardest word it contains. */
  r: number;
  /** Situational deck ids this sentence belongs to. */
  topics: string[];
  /** Tatoeba sentence id — the licence requires attribution, and this keeps it traceable. */
  tid: number;
}

/**
 * Which part of the home page a deck belongs to.
 *
 * A KEY, not a heading. `group` is prose written for a reader in that reader's language — "By
 * length", "按长度", "長さ別" — and web/app.ts matched its tiles against the English spellings,
 * which worked for exactly one course out of four (MYN-148). This is the stable half, and it is
 * the same distinction item-key.ts drew for progress: a display string belongs to a language and
 * cannot identify anything.
 *
 * scripts/lib/sections.mjs holds the generators' copy; test/sections asserts the two agree.
 */
export type Section = 'words' | 'length' | 'in-context' | 'questions' | 'situations';

export interface SentenceDeck {
  id: string;
  name: string;
  blurb: string;
  /** Heading this deck sits under in the deck list. Shown, never matched on. */
  group: string;
  /**
   * Which home section this deck is part of.
   *
   * Optional only for a corpus downloaded before this shipped — a phone keeps its own and does not
   * refetch it because the app updated. allDecks() reads the id when it is missing.
   */
  section?: Section;
  /** Indices into `Sentences.sentences`, already easy -> hard. */
  sentences: number[];
}

/** A word's reading and meaning, for words the graded vocabulary does not carry. */
/**
 * What a sentence or story knows about a word its corpus does not otherwise teach.
 *
 * Written by build-sentences.mjs and nothing else, which is why every reader must treat the
 * glossary as possibly absent — see lookupWord. Declaring it required did not make it so; it only
 * meant the compiler agreed with an assumption the data had already broken.
 */
export interface GlossaryEntry {
  p: string;
  tones: number[];
  e: string[];
}

export interface Sentences {
  version: number;
  generatedAt: string;
  source: { name: string; url: string; license: string; attribution: string };
  decks: SentenceDeck[];
  sentences: Sentence[];
  /**
   * Glosses for sentence words the vocabulary does not cover — about 8.9% of occurrences. Keyed by
   * word rather than inlined per sentence, so it costs 1472 entries instead of 42581 copies.
   */
  /** Absent on a corpus whose builder does not write one — three of the four do not. */
  glossary?: Record<string, GlossaryEntry>;
}

/** One line of a story: the Chinese, its translation, and its words. */
export interface StoryLine {
  s: string;
  en: string;
  w: SentenceWord[];
}

export interface Story {
  id: string;
  /** Chinese title. */
  title: string;
  titleEn: string;
  /** 1 is nursery level, held to the Top 100; 2 opens up to the Top 500. */
  level: number;
  blurb: string;
  lines: StoryLine[];
}

export interface Stories {
  version: number;
  generatedAt: string;
  stories: Story[];
  /** Words the graded vocabulary does not carry, so every word in a story can still be tapped. */
  /** Absent on a corpus whose builder does not write one — three of the four do not. */
  glossary?: Record<string, GlossaryEntry>;
}

/**
 * Why a character looks the way it does.
 *
 * `sound` is the useful majority: a part that carries the meaning beside a part that carries the
 * pronunciation. `picture` and `idea` cover the older characters that were drawn rather than built.
 */
export type Hint = (
  /**
   * A meaning part beside a sound part, and the sound part's own reading.
   *
   * CHINESE ONLY, and that is a claim about evidence rather than about languages. The reading here
   * is pinyin, which is not this app's only course's sound any more; and in Japanese the phonetic
   * series often survives the character while the ON reading does not -- 読 is ドク where its
   * phonetic 売 is バイ. Asserting the sound there would teach something false, so a Japanese
   * course gets `parts` instead: the same structure, without the claim (MYN-165).
   */
    | { kind: 'sound'; semantic: string; phonetic: string; phoneticReading: string; sense: string | null }
    /** The two halves a character is built from, with no claim about how either is pronounced. */
    | { kind: 'parts'; semantic: string; other: string; sense: string | null }
    | { kind: 'picture' | 'idea'; sense: string }
    /**
     * Nothing to say about the shape -- only about the sound.
     *
     * A kanji with no entry in either decomposition source still has readings, and the readings
     * are the half a Japanese learner needs more: 150 of this course's 1,077 characters have no
     * structural hint, and they are not the rare ones -- 事 is the 49th word's, 買 the 67th's.
     * Without this kind they would have had no panel at all, and the readings would have shipped
     * only for the characters that happened to decompose cleanly. With it the course explains
     * 1,076 of 1,077; the holdout is 々, which is read as whatever it repeats.
     */
    | { kind: 'readings' }
) & {
  /**
   * How the kanji is read on its own: the Chinese-derived readings and the native Japanese ones.
   *
   * JAPANESE ONLY, and it is the question the Chinese hints never have to answer. 生 is read セイ,
   * ショウ, い.きる, う.まれる and more besides, and a learner meeting 先生 after 生まれる has no
   * way to know those are the same character doing the same job -- the app said nothing, and the
   * card taught each word's reading as an unrelated fact (MYN-176).
   *
   * Optional everywhere, because a Chinese course has no on and kun to give, and because a kanji
   * KANJIDIC does not carry gets a hint about its shape and no readings rather than no hint.
   */
  readings?: KanjiReadings;
};

/**
 * One kanji's readings, at most three of each, the ones this course actually uses first.
 *
 * WRITTEN AS KANJIDIC WRITES THEM apart from the hyphens: on in katakana, kun in hiragana with a
 * `.` where the kanji's own reading stops and the trailing kana begin -- い.きる is い written with
 * 生 and きる written in kana after it. The app renders what follows the dot faintly rather than
 * dropping it, because a kun reading without its okurigana is not a word you can say.
 */
export interface KanjiReadings {
  /** On'yomi, from Chinese. Katakana, as every Japanese dictionary writes them. */
  on: string[];
  /** Kun'yomi, the native reading. Hiragana, with the okurigana boundary marked. */
  kun: string[];
}

/**
 * The order a character is written in: one polyline per stroke, in order.
 *
 * Optional, and absent for a pair whose writing system has no such data — the app then does not
 * offer the panel at all. See scripts/build-strokes.mjs for why these are the medians rather than
 * the outlines.
 */
export interface Strokes {
  version: number;
  generatedAt: string;
  /** The coordinate box the points live in. */
  box: number;
  /** How to get from the source's font coordinates into SVG's y-down space. */
  transform: string;
  source: Record<string, string>;
  /** Character -> its strokes, each a list of [x, y] points along the pen's path. */
  strokes: Record<string, [number, number][][]>;
}

export interface Hints {
  version: number;
  generatedAt: string;
  source: { name: string; url: string; derivedFrom: string; license: string };
  hints: Record<string, Hint>;
}

/** One thing the tutor can explain: a grammar point, or a pair of words people mix up. */
export interface TutorEntry {
  id: string;
  kind: 'point' | 'pair';
  title: string;
  /** The explanation itself. */
  body: string;
  /** Ways a learner might phrase the question; retrieval matches against these. */
  ask: string[];
  /** The Chinese this entry is about — typing any of it should find the entry. */
  words: string[];
  /** Sentence indices from the shipped corpus that show the point in use. */
  examples: number[];
}

export interface Tutor {
  version: number;
  generatedAt: string;
  entries: TutorEntry[];
}

/** One completed placement test, as it is stored. */
export interface TestResult {
  id: number;
  takenAt: number;
  /** Estimated words known, from src/shared/quiz.ts. */
  known: number;
  asked: number;
  right: number;
  /** Per-band scores, so an old result can be re-read without re-deriving it. */
  bands: { from: number; to: number; asked: number; right: number }[];
}

/** How a card is asked. */
export type CardMode = 'hanzi-en' | 'en-hanzi' | 'audio';

export const CARD_MODES: { id: CardMode; name: string; hint: string }[] = [
  { id: 'hanzi-en', name: '汉字 → English', hint: 'See the characters, recall the meaning.' },
  { id: 'en-hanzi', name: 'English → 汉字', hint: 'See the meaning, produce the characters.' },
  { id: 'audio', name: 'Listen → meaning', hint: 'Hear the word, recall what it means.' },
];

/** The two kinds of studyable item. Their ids are separate spaces, so cards carry both. */
export type ItemKind = 'word' | 'sentence';

/** A card's identity: which kind, and its index within that kind's array. */
export interface CardId {
  kind: ItemKind;
  id: number;
}

/** "word:42" — the namespaced key used by the scheduler and the progress table. */
export const cardKey = (kind: ItemKind, id: number): string => `${kind}:${id}`;

export function parseCardKey(key: string): CardId {
  const [kind, id] = key.split(':');
  return { kind: (kind === 'sentence' ? 'sentence' : 'word') as ItemKind, id: Number(id) };
}

/** What the learner pressed after seeing the answer. */
export type Grade = 'again' | 'hard' | 'good' | 'easy';

/** Everything the scheduler needs to place a card in time. Persisted per word. */
export interface CardState {
  /** Consecutive successful reviews; reset to 0 by `again`. */
  reps: number;
  /** How many times this card has been forgotten after graduating. */
  lapses: number;
  /** SM-2 style multiplier, clamped to [1.3, 3.0]. */
  ease: number;
  /** Current spacing in minutes. */
  intervalMin: number;
  /** Epoch millis when the card next comes up. */
  dueAt: number;
}

export interface ReviewLog {
  wordId: number;
  deckId: string;
  mode: CardMode;
  grade: Grade;
  at: number;
}

/** Per-deck progress, as shown on the deck list. */
export interface DeckProgress {
  deckId: string;
  total: number;
  seen: number;
  /** Cards with an interval of a day or more — genuinely retained rather than just met. */
  learned: number;
  due: number;
  /** Declared already known, and so never scheduled. Counted inside `learned` as well. */
  known: number;
}

/** A sentence the model wrote for this learner, out of words this learner knows. */
export interface MadeSentence {
  id: number;
  s: string;
  e: string;
  words: { t: string; id: number }[];
  madeAt: number;
}

/** A story the model wrote for this learner, in the same shape as the handwritten ones. */
export interface MadeStory {
  id: number;
  title: string;
  titleEn: string;
  blurb: string;
  lines: { s: string; e: string; words: { t: string; id: number }[] }[];
  madeAt: number;
}

export type { Language, Pair } from './pair.js';
